import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import {
    useCreateEnvironment,
    useCreateEnvironmentFromProject,
    useStartEnvironment,
    type PlainEnvironment,
    type UseCreateBlankEnvironmentParamsFromContextUrl,
    type UseCreateEnvironmentParamsFromContextUrl,
} from "@/queries/environment-queries";
import { useProject } from "@/queries/project-queries";
import { getDetailsURL } from "@/routes/environments/details-url";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";

type EnvironmentLifetimeExceededModalProps = {
    environment: PlainEnvironment;
    open: boolean;
    onClose: () => void;
};

export const EnvironmentLifetimeExceededModal: FC<EnvironmentLifetimeExceededModalProps> = ({
    environment,
    open,
    onClose,
}) => {
    const { refetch: refetchProject } = useProject(environment?.metadata?.projectId);
    const startEnvironment = useStartEnvironment();
    const createEnvironment = useCreateEnvironment();
    const createEnvironmentFromProject = useCreateEnvironmentFromProject();
    const navigate = useNavigate();
    const { toast } = useToast();

    const handleContinue = useCallback(async () => {
        try {
            await startEnvironment.mutateAsync({ environmentId: environment.id, skipPolicyCheck: true });
        } catch (error) {
            toast({
                title: "Failed to start environment",
                description: formatError(error),
            });
        }
        onClose();
    }, [onClose, startEnvironment, environment.id, toast]);

    const handleCreateNewEnv = useCallback(async () => {
        try {
            let newEnvironment: PlainEnvironment;
            if (environment.metadata?.projectId) {
                const { data: project } = await refetchProject();
                if (!project) {
                    return;
                }

                newEnvironment = await createEnvironmentFromProject.mutateAsync({
                    project,
                    environmentClassId: environment.spec?.machine?.class,
                });
            } else {
                const initializerSpecs = environment.spec?.content?.initializer?.specs;
                let createOptions:
                    | UseCreateEnvironmentParamsFromContextUrl
                    | UseCreateBlankEnvironmentParamsFromContextUrl;
                if (!initializerSpecs?.length) {
                    createOptions = { type: "blank", classID: environment.spec!.machine!.class };
                } else {
                    let contextURL: string;
                    if (initializerSpecs[0].spec.case === "git") {
                        contextURL = initializerSpecs[0].spec.value.remoteUri;
                    } else {
                        contextURL = initializerSpecs[0].spec.value!.url;
                    }
                    createOptions = {
                        type: "contextUrl",
                        classID: environment.spec!.machine!.class,
                        contextURL,
                    };
                }
                newEnvironment = await createEnvironment.mutateAsync(createOptions);
                navigate(getDetailsURL({ environment: newEnvironment }));
            }
        } catch (error) {
            toast({
                title: "Failed to create a new environment",
                description: formatError(error),
            });
        }
        onClose();
    }, [
        onClose,
        environment.metadata?.projectId,
        environment.spec,
        refetchProject,
        createEnvironmentFromProject,
        createEnvironment,
        navigate,
        toast,
    ]);

    return (
        <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-md" data-track-location={TrackLocations.EnvironmentLifetimeExceededModal}>
                <DialogHeader>
                    <DialogTitle>Environment Lifetime Exceeded</DialogTitle>
                </DialogHeader>
                <DialogBody>
                    Your environment is exceeding your organization policy. Ensure you&apos;ve backed up your work and
                    create a new environment.
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="secondary"
                        onClick={handleContinue}
                        data-tracking-id="restart-anyway-environment-lifetime-exceeded-modal"
                    >
                        Restart Anyway
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleCreateNewEnv}
                        data-tracking-id="create-new-environment-environment-lifetime-exceeded-modal"
                    >
                        Create New Environment
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
